/*
 * Sample data for the Supporter Response blocks and templates (Figma: 33 / Templates / Supporter Response).
 * A supporter is a person with a profile and tags; a conversation is one row in the contact list. The same
 * supporter can have more than one conversation (Brandon and Gloria shared twice).
 */
export type ChatThreadKind = 'mission' | 'involvement'

export interface ChatThreadItem {
    kind: ChatThreadKind
    journey: string
    message: string
}

export interface ChatTagItem {
    label: string
    removable?: boolean
}

export interface ChatSupporter {
    id: string
    initials: string
    name: string
    fullName: string
    location: string
    messengerName: string
    email: string
    /* A photo the supporter shared, shown in ContactPhoto and ContactAvatar. Without one, the initials show. */
    photo?: string
    tags: ChatTagItem[]
    suggestedTags: ChatTagItem[]
}

export interface ChatReply {
    channel: 'messenger' | 'email'
    label: string
    message?: string
}

export interface ChatConversation {
    id: string
    supporterId: string
    group: 'today' | 'yesterday'
    time?: string
    topic: string
    preview: string
    unread: boolean
    done: boolean
    thread: ChatThreadItem[]
    replies: ChatReply[]
    draft: string
    subject: string
}

export const CHAT_SUPPORTERS: ChatSupporter[] = [
    {
        id: 'brandon',
        initials: 'BS',
        name: 'Brandon',
        fullName: 'Brandon Smith',
        location: 'Portland, Oregon',
        messengerName: 'Brandon Smith',
        email: 'brandon@example.com',
        photo: '/images/people/man-7.jpg',
        tags: [{ label: 'Host Event' }, { label: 'Beneficiary' }, { label: 'COPD Community' }, { label: 'Monthly Donor', removable: true }],
        suggestedTags: [{ label: 'Planned Giving' }, { label: 'Primary Care' }, { label: 'Grandmother Connection', removable: true }]
    },
    {
        id: 'gloria',
        initials: 'GL',
        name: 'Gloria',
        fullName: 'Gloria Lopez',
        location: 'Austin, Texas',
        messengerName: 'Gloria Lopez',
        email: 'gloria@example.com',
        photo: '/images/people/woman-3.jpg',
        tags: [{ label: 'COPD Community' }, { label: 'Caregiver', removable: true }],
        suggestedTags: [{ label: 'Family Support' }, { label: 'Grandmother Connection', removable: true }]
    },
    {
        id: 'jamie',
        initials: 'JM',
        name: 'Jamie',
        fullName: 'Jamie Miller',
        location: 'Columbus, Ohio',
        messengerName: 'Jamie Miller',
        email: 'jamie@example.com',
        tags: [{ label: 'Researcher' }, { label: 'Volunteer', removable: true }],
        suggestedTags: [{ label: 'Survey Partner', removable: true }]
    },
    {
        id: 'alex',
        initials: 'AL',
        name: 'Alex',
        fullName: 'Alex Lee',
        location: 'Denver, Colorado',
        messengerName: 'Alex Lee',
        email: 'alex@example.com',
        tags: [{ label: 'Corporate Partner' }, { label: 'Host Event', removable: true }],
        suggestedTags: [{ label: 'Team Volunteering', removable: true }]
    },
    {
        id: 'morgan',
        initials: 'MG',
        name: 'Morgan',
        fullName: 'Morgan Grant',
        location: 'Raleigh, North Carolina',
        messengerName: 'Morgan Grant',
        email: 'morgan@example.com',
        tags: [{ label: 'Monthly Donor' }, { label: 'Research Fund', removable: true }],
        suggestedTags: [{ label: 'Advisory Group', removable: true }]
    }
]

const BRANDON_REPLY = "Hi Brandon, thank you so much for sharing your story. Losing your grandmother to COPD and wanting to protect future generations is incredibly moving. We’d be honored to be named a beneficiary in your will — your generosity will help ensure others don’t have to suffer the same loss. We’ll follow up with more details."

export const CHAT_CONVERSATIONS: ChatConversation[] = [
    {
        id: 'brandon-today',
        supporterId: 'brandon',
        group: 'today',
        time: '4m',
        topic: 'mission statement',
        preview: "Would love to explore how we could make you a beneficiary in my will",
        unread: false,
        done: false,
        thread: [
            { kind: 'mission', journey: 'You are not alone', message: "I lost my grandmother to COPD and want to ensure future generations don’t suffer." },
            { kind: 'involvement', journey: 'Walk Together', message: "Would love to explore how I could make you a beneficiary in my will" }
        ],
        replies: [],
        draft: BRANDON_REPLY,
        subject: 'A heartfelt thank you, Brandon'
    },
    {
        id: 'gloria-today',
        supporterId: 'gloria',
        group: 'today',
        time: '22m',
        topic: 'mission statement',
        preview: "I lost my grandmother to COPD and want to help",
        unread: true,
        done: false,
        thread: [
            { kind: 'mission', journey: 'You are not alone', message: "I lost my grandmother to COPD and want to help other families going through it." }
        ],
        replies: [],
        draft: "Hi Gloria, thank you for sharing your grandmother’s story with us. We’re so sorry for your loss, and we’re grateful you want to help other families facing COPD. Our Family Support Circle connects people like you with families who are just starting out. Would you like us to send you the details?",
        subject: 'Thank you for sharing your story, Gloria'
    },
    {
        id: 'brandon-yesterday',
        supporterId: 'brandon',
        group: 'yesterday',
        topic: 'mission statement',
        preview: "Would love to explore how we could help",
        unread: false,
        done: true,
        thread: [
            { kind: 'involvement', journey: 'Walk Together', message: "Would love to explore how we could help with the spring walk in Portland." }
        ],
        replies: [
            { channel: 'messenger', label: 'You replied · 4:12 PM, Aug 15, 2026', message: "Hi Brandon, we’d love your help with the spring walk! Volunteers meet at Laurelhurst Park at 8 AM. We’ll send you the full schedule this week." }
        ],
        draft: "Hi Brandon, thanks again for offering to help with the spring walk. Would you like to lead a team this year? We’ll share everything you need to get started.",
        subject: 'Your spring walk team, Brandon'
    },
    {
        id: 'jamie-yesterday',
        supporterId: 'jamie',
        group: 'yesterday',
        topic: 'mission statement',
        preview: "Proposed conducting surveys for deeper insight",
        unread: false,
        done: true,
        thread: [
            { kind: 'mission', journey: 'You are not alone', message: "I proposed conducting surveys with patients and caregivers for deeper insight into the support they need." }
        ],
        replies: [
            { channel: 'email', label: 'You copied the response and replied via email' }
        ],
        draft: "Hi Jamie, thank you for the thoughtful idea. Surveys with patients and caregivers would help us shape our programs. Could we set up a short call with our research team next week?",
        subject: 'Your survey idea, Jamie'
    },
    {
        id: 'gloria-yesterday',
        supporterId: 'gloria',
        group: 'yesterday',
        topic: 'mission statement',
        preview: "I lost my grandmother to COPD and want to take part",
        unread: false,
        done: true,
        thread: [
            { kind: 'involvement', journey: 'Walk Together', message: "I lost my grandmother to COPD and want to take part in the walk in her memory." }
        ],
        replies: [
            { channel: 'messenger', label: 'You replied · 2:48 PM, Aug 15, 2026', message: "Hi Gloria, we’d be honored to have you walk in your grandmother’s memory. You can add her name to your team page when you sign up." }
        ],
        draft: "Hi Gloria, we’re so glad you’re joining the walk. Would you like a memory ribbon with your grandmother’s name for the day?",
        subject: 'Walking in her memory, Gloria'
    },
    {
        id: 'alex-yesterday',
        supporterId: 'alex',
        group: 'yesterday',
        topic: 'mission statement',
        preview: "Interested in incorporating them into our plans",
        unread: false,
        done: true,
        thread: [
            { kind: 'involvement', journey: 'Walk Together', message: "We’re setting our team’s volunteer days for next year and are interested in incorporating them into our plans." }
        ],
        replies: [
            { channel: 'messenger', label: 'You replied · 11:05 AM, Aug 15, 2026', message: "Hi Alex, that’s wonderful news. Team volunteer days can be built around our community walks or our support groups. We’ll send you some options." }
        ],
        draft: "Hi Alex, here are three volunteer days your team could join next year. Let us know which ones work best and we’ll hold the spots.",
        subject: 'Volunteer days for your team, Alex'
    },
    {
        id: 'morgan-yesterday',
        supporterId: 'morgan',
        group: 'yesterday',
        topic: 'mission statement',
        preview: "Suggested setting milestones to track progress",
        unread: false,
        done: true,
        thread: [
            { kind: 'mission', journey: 'You are not alone', message: "Suggested setting milestones to track progress toward the research fund goal." }
        ],
        replies: [
            { channel: 'messenger', label: 'You replied · 9:30 AM, Aug 15, 2026', message: "Hi Morgan, great suggestion. We’ll share quarterly milestones for the research fund so every supporter can follow the progress." }
        ],
        draft: "Hi Morgan, our first research fund milestone is live. Thank you for suggesting it. Would you like updates each quarter?",
        subject: 'Research fund milestones, Morgan'
    }
]

/* The reply shown in the Figma sent state. */
export const SENT_REPLY_LABEL = 'You replied · 12:36 AM, Aug 16, 2026'

export const WEBSITE_LINKS = Array.from({ length: 20 }, (_, index) => ({
    id: index + 1,
    url: `goodunited.com/link${index + 1}`,
    description: 'Main website and campaign information'
}))

export const BRAND_FILES = ['CompanyReport.pdf', 'CompanyBrochure.pdf', 'CompanyPresentation.pptx']

export const WRITING_TONES = ['Professional', 'Warm', 'Friendly', 'Formal', 'Casual']
